import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { DialogService } from '@app/core/services/dialog/dialog.service';
import { ButtonColor } from '@components/buttons/button/button.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';

export interface Tab {
  id: string;
  index: number;
  label: string;
  template?: TemplateRef<any>;
  hasContent: boolean;
  canClose: boolean;
  isCloseVisible: boolean;
  hasError?: boolean;
}

export interface TabReorderedEvent {
  previousIndex: number;
  currentIndex: number;
  tab: Tab;
}

export interface TranslateDialog {
  titleDialog?: string;
  messageDialog?: string;
  confirmDialog?: string;
  messageConfirmDialog?: string;
}

@Component({
  selector: 'vfs-page-group',
  templateUrl: './page-group.component.html',
  styleUrls: ['./page-group.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PageGroupComponent implements OnInit, OnChanges {
  @Input() tabs: Array<Tab>;
  @Input() entitity: string = 'Tab';
  @Input() defaultContent: TemplateRef<any>;
  @Input() initialIndex: number = 0;
  @Input() disabledButton: boolean;
  @Input() canCloseLastTab: boolean = true;
  @Input() lazyContent: boolean = false;
  @Input() dropListPrefix: string = 'list';
  @Input() maxTabs: number = -1;
  @Input() translatesDialog: TranslateDialog;
  @Input() animationDuration: number;
  @Input() minHeight: string;

  @Output() tabAdded = new EventEmitter<Tab>();
  @Output() tabClosed = new EventEmitter<Tab>();
  @Output() tabChanged = new EventEmitter<Tab>();
  @Output() tabReordered = new EventEmitter<TabReorderedEvent>();

  dirty: boolean = false;
  selectedIndex: number = 0;

  constructor(private dialogService: DialogService, protected translateService: TranslateService) {}

  ngOnInit(): void {
    this.translatesDialog = this.getDefaultTranslateDialog();

    this.tabs.forEach((tab: Tab) => {
      if (!tab.template) {
        tab.template = this.defaultContent;
      }
    });

    this.selectedIndex = this.initialIndex;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tabs && !changes.tabs.firstChange) {
      this.tabs.forEach((tab: Tab) => {
        if (!tab.template) {
          tab.template = this.defaultContent;
        }
      });
    }
  }

  addTab(): void {
    let tab: Tab;
    tab = {
      id: uuid(),
      index: this.tabs.length,
      label: `${this.entitity} ${this.tabs.length + 1}`,
      template: this.defaultContent,
      hasContent: false,
      canClose: true,
      isCloseVisible: true,
      hasError: false
    };

    if (this.maxTabs === -1 || this.tabs.length < this.maxTabs) {
      this.tabs.push(tab);
      this.selectedIndex = this.tabs.length - 1;
      this.markAsDirty();

      this.tabAdded.emit(tab);
    }
  }

  closeTab(tab: Tab): void {
    if (tab.hasContent) {
      if (!tab.canClose) {
        this.dialogService.alert(
          this.translateService.instant(this.translatesDialog.titleDialog),
          this.translateService.instant(this.translatesDialog.messageDialog)
        );
      } else {
        this.openConfirmDialog().subscribe(response => {
          if (response) {
            this.removeTab(tab);
          }
        });
      }
    } else {
      this.removeTab(tab);
    }
  }

  removeTab(tab: Tab): void {
    const indexOfTabClosed = this.tabs.indexOf(tab);
    this.tabs.splice(indexOfTabClosed, 1);

    this.tabs.length > indexOfTabClosed
      ? (this.selectedIndex = indexOfTabClosed)
      : (this.selectedIndex = indexOfTabClosed - 1);

    this.markAsDirty();

    this.tabClosed.emit(tab);
  }

  getAllListConnections(index: number): Array<string> {
    const connections: Array<string> = [];

    for (let i = 0; i < this.tabs.length; i++) {
      if (i !== index) {
        connections.push(this.generateDropListId(i));
      }
    }
    return connections;
  }

  drop(event: CdkDragDrop<string[]>): void {
    const previousIndex: number = Number(event.previousContainer.id.replace(this.dropListPrefix + '-', ''));
    const currentIndex: number = Number(event.container.id.replace(this.dropListPrefix + '-', ''));

    if (
      previousIndex != NaN &&
      currentIndex != NaN &&
      previousIndex != undefined &&
      currentIndex != undefined &&
      previousIndex != currentIndex
    ) {
      moveItemInArray(this.tabs, previousIndex, currentIndex);

      this.tabs.forEach((tab, i) => (tab.index = i));

      this.markAsDirty();
      this.tabReordered.emit({ previousIndex, currentIndex, tab: this.tabs[currentIndex] });
      // Change focus tab, forced to avoid Angular Ivi render bug
      // Link here: https://github.com/angular/components/issues/13572
      this.changedTab(this.tabs.find((tab: Tab, i: number) => currentIndex === i));
    }
  }

  changedTab(tab: Tab): void {
    this.selectedIndex = tab.index;
    this.tabChanged.emit(tab);
  }

  generateDropListId(index: number): string {
    return this.dropListPrefix + '-' + index;
  }

  private markAsDirty(): void {
    this.dirty = true;
  }

  private openConfirmDialog(): Observable<boolean> {
    const messageConfirm = this.translatesDialog.messageConfirmDialog
      ? this.translateService.instant(this.translatesDialog.messageConfirmDialog)
      : '';
    return this.dialogService.confirm(
      { label: this.translateService.instant(this.translatesDialog.confirmDialog) },
      messageConfirm,
      {
        label: this.translateService.instant('core.confirm.yesDelete'),
        color: ButtonColor.WARN
      },
      { label: this.translateService.instant('core.general.cancel') }
    );
  }

  protected getDefaultTranslateDialog(): TranslateDialog {
    return {
      titleDialog: this.translatesDialog.titleDialog ?? 'component.tabs.error.cannotDelete',
      messageDialog: this.translatesDialog.messageDialog ?? 'component.tabs.error.cannotDeleteMessage',
      confirmDialog: this.translatesDialog.confirmDialog ?? 'component.tabs.deleteConfirmMessage',
      messageConfirmDialog: this.translatesDialog.messageConfirmDialog ?? 'core.leave.loseChanges'
    };
  }
}
